// src/HomePage/Game/BibleQuiz/BibleQuiz.js
import React, { useState, useEffect, useRef } from 'react';
import { updateQuizScoreFirestore, fetchStudentRewardsFirestore } from '../../../FirebaseService';
import allQuestions from './bibleQuestions.json';
import './BibleQuiz.css';

const BibleQuiz = ({ studentId, isCatechist = false, onScoreUpdate }) => {
  const shuffleArray = (array) => {
    let shuffled = array.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const [isStarted, setIsStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [quizRewardMessage, setQuizRewardMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const [rewardAttempts, setRewardAttempts] = useState(0);
  const [effectiveUserId, setEffectiveUserId] = useState('');

  // 🔒 KHÓA CHỐNG GỌI TRANSACTION TRÙNG LẶP KHI RENDER
  const hasAwardedRef = useRef(false);

  useEffect(() => {
    const uid = isCatechist 
      ? (localStorage.getItem('uid') || studentId) 
      : (localStorage.getItem('studentId') || studentId);

    if (!uid || uid === 'GLV') {
      setEffectiveUserId('');
      setQuizRewardMessage('Vui lòng đăng nhập để tham gia chơi và nhận thưởng.');
      return;
    }

    setEffectiveUserId(uid);

    fetchStudentRewardsFirestore(uid, isCatechist).then((data) => {
      setRewardAttempts(data.quizAttemptsToday);
      if (data.quizAttemptsToday >= 3) {
        setQuizRewardMessage('💡 Bạn đã đạt tối đa 3 lượt nhận thưởng hôm nay. Bạn vẫn có thể chơi tự do bao nhiêu lần tùy thích!');
      } else {
        setQuizRewardMessage('');
      }
    });
  }, [studentId, isCatechist]);

  // Bộ đếm thời gian
  useEffect(() => {
    let timer;
    if (isStarted && !showResults && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && !showResults) {
      setShowResults(true);
    }
    return () => clearInterval(timer);
  }, [isStarted, showResults, timeLeft]);

  // 🌟 CHỈ GỌI LƯU ĐIỂM 1 LẦN DUY NHẤT KHI SHOW RESULTS = TRUE
  useEffect(() => {
    if (showResults && !hasAwardedRef.current && effectiveUserId) {
      hasAwardedRef.current = true; // Khóa ngay lập tức
      const finalScore = answers.reduce((score, answer, index) => {
        return answer === questions[index]?.correct ? score + 1 : score;
      }, 0);

      awardQuizStars(finalScore);
    }
  }, [showResults, effectiveUserId]);

  const startQuiz = () => {
    if (!effectiveUserId) {
      alert('Vui lòng đăng nhập trước khi chơi!');
      return;
    }

    hasAwardedRef.current = false; // Mở khóa cho ván mới
    const randomQuestions = shuffleArray(allQuestions).slice(0, 5);
    setQuestions(randomQuestions);
    setAnswers(Array(randomQuestions.length).fill(null));
    setIsStarted(true);
    setTimeLeft(30);
    setCurrentQuestion(0);
    setSelectedOption(null);
    setShowResults(false);
    setQuizRewardMessage('');
  };

  const handleSelect = (option) => {
    setSelectedOption(option);
  };

  const nextQuestion = () => {
    if (selectedOption === null) {
      alert('Vui lòng chọn một đáp án trước khi tiếp tục!');
      return;
    }

    const newAnswers = [...answers];
    newAnswers[currentQuestion] = selectedOption;
    setAnswers(newAnswers);
    setSelectedOption(null);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const awardQuizStars = async (score) => {
    if (!effectiveUserId) return;

    const rewardResult = await updateQuizScoreFirestore(effectiveUserId, score, isCatechist);

    if (rewardResult.success) {
      setQuizRewardMessage(`🎉 Chúc mừng! Bạn nhận được thêm ${rewardResult.added} ⭐ cho lượt chơi này.`);
      setRewardAttempts(rewardResult.attempts);
      if (typeof onScoreUpdate === 'function') {
        onScoreUpdate();
      }
    } else if (rewardResult.isMaxAttempts) {
      setQuizRewardMessage('🎮 Bạn đã hoàn thành ván chơi! (Lượt chơi này không cộng sao vì bạn đã nhận đủ 3 lần thưởng hôm nay).');
    } else {
      setQuizRewardMessage(rewardResult.message || 'Lỗi khi cập nhật thưởng.');
    }
  };

  const resetQuiz = () => {
    hasAwardedRef.current = false; // Reset khóa
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedOption(null);
    setShowResults(false);
    setQuizRewardMessage('');
    setTimeLeft(30);
    setIsStarted(false);

    if (effectiveUserId) {
      fetchStudentRewardsFirestore(effectiveUserId, isCatechist).then((data) => {
        setRewardAttempts(data.quizAttemptsToday);
      });
    }
  };

  if (!isStarted) {
    return (
      <div className="quiz-container">
        <h2>📖 Đố Vui Kinh Thánh</h2>

        {rewardAttempts >= 3 ? (
          <div className="unlimited-notice">
            ⭐ <b>Đã nhận thưởng 3/3 lần hôm nay.</b>
            <p>Bạn vẫn có thể tiếp tục chơi không giới hạn để rèn luyện tri thức Kinh Thánh!</p>
          </div>
        ) : (
          <p className="attempt-status-badge">
            🏆 Lượt nhận thưởng hôm nay: <b>{rewardAttempts}/3</b>
          </p>
        )}

        {quizRewardMessage && <p className="reward-message">{quizRewardMessage}</p>}

        <div className="instructions">
          <p><b>Hướng dẫn thể lệ:</b></p>
          <ul>
            <li>⏱️ Bạn có <b>30 giây</b> để hoàn thành <b>5 câu hỏi</b>.</li>
            <li>⭐ Mỗi câu trả lời đúng được cộng <b>1 Ngôi sao Đức tin</b>.</li>
            <li>🎁 Tối đa <b>3 lượt nhận thưởng/ngày</b> (lên tới 15 ⭐).</li>
            <li>🔄 <b>Không giới hạn số lần chơi:</b> Bạn có thể chơi lại nhiều lần để ôn tập!</li>
          </ul>
        </div>

        <button className="next-button start-btn" onClick={startQuiz}>
          🚀 Bắt Đầu Chơi Ngay
        </button>
      </div>
    );
  }

  // MÀN HÌNH KẾT QUẢ (Không chứa lệnh gọi hàm awardQuizStars trực tiếp ở đây nữa)
  if (showResults) {
    const score = answers.reduce((s, a, i) => (a === questions[i]?.correct ? s + 1 : s), 0);

    return (
      <div className="quiz-container">
        <h2>Kết Quả Đố Vui Kinh Thánh</h2>
        <div className="score-badge">
          Trả lời đúng: <b>{score} / {questions.length}</b> câu
        </div>

        {quizRewardMessage && <p className="reward-message highlight">{quizRewardMessage}</p>}

        <h3 style={{ marginTop: '20px', textAlign: 'left' }}>Xem lại đáp án:</h3>
        {questions.map((q, index) => (
          <div key={index} style={{ marginBottom: '16px', textAlign: 'left', background: '#fff', padding: '12px', borderRadius: '10px' }}>
            <p className="quiz-question" style={{ fontSize: '1rem', margin: '0 0 6px 0' }}>
              <b>Câu {index + 1}:</b> {q.question}
            </p>
            <p style={{ margin: '2px 0', fontSize: '0.9rem' }}>Lựa chọn của bạn: <i>{answers[index] || 'Chưa chọn'}</i></p>
            <p style={{ margin: '2px 0', fontSize: '0.9rem', color: '#16a34a' }}>Đáp án đúng: <b>{q.correct}</b></p>
            <p className={answers[index] === q.correct ? 'correct' : 'incorrect'} style={{ margin: '4px 0 0 0', fontWeight: 'bold' }}>
              {answers[index] === q.correct ? '✅ Đúng' : '❌ Sai'}
            </p>
          </div>
        ))}

        <button className="next-button" onClick={resetQuiz}>
          🔄 Chơi Lại Ván Khác
        </button>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      <h2>📖 Đố Vui Kinh Thánh</h2>
      <p className="timer">⏱️ Thời gian còn lại: <b>{timeLeft}</b> giây</p>
      
      <p className="quiz-question">
        <b>Câu {currentQuestion + 1}/5:</b> {questions[currentQuestion]?.question}
      </p>

      <div className="quiz-options">
        {questions[currentQuestion]?.options.map((option, index) => (
          <div
            key={index}
            className={`quiz-option ${selectedOption === option ? 'selected' : ''}`}
            onClick={() => handleSelect(option)}
          >
            {option}
          </div>
        ))}
      </div>

      <button className="next-button" onClick={nextQuestion}>
        {currentQuestion < questions.length - 1 ? 'Câu Tiếp Theo ➔' : 'Hoàn Thành ✔️'}
      </button>
    </div>
  );
};

export default BibleQuiz;